export interface PropertyItem {
  id: string
  name?: string | null
  address?: string | null
  property_type?: string | null
  city?: string | null
  state?: string | null
  zip?: string | null
  zip_code?: string | null
}

export interface ProjectItem {
  id?: string | null
  address?: string | null
  type?: string | null
  work_type?: string | null
  city?: string | null
  state?: string | null
  zip?: string | null
}

export interface PropertyProjectOption {
  id: string
  label: string
  subtitle?: string
  searchText: string
  raw: PropertyItem | ProjectItem
}

function buildSearchText(parts: Array<string | null | undefined>): string {
  return parts.filter(Boolean).join(' ').toLowerCase()
}

export function buildPropertyOptions(properties: PropertyItem[]): PropertyProjectOption[] {
  return properties.map((property) => {
    const label = property.name?.trim() || property.address?.trim() || 'Unnamed property'
    const searchText = buildSearchText([
      property.name,
      property.address,
      property.property_type,
      property.city,
      property.state,
      property.zip,
      property.zip_code,
    ])
    const subtitle =
      property.address && property.address !== label ? property.address : property.property_type

    return {
      id: property.id,
      label,
      subtitle: subtitle || undefined,
      searchText,
      raw: property,
    }
  })
}

export function buildProjectOptions(projects: ProjectItem[]): PropertyProjectOption[] {
  return projects.map((project, index) => {
    const id = project.id || `project-${index}`
    const label = project.address?.trim() || project.type?.trim() || `Project ${index + 1}`
    const searchText = buildSearchText([
      project.type,
      project.work_type,
      project.address,
      project.city,
      project.state,
      project.zip,
    ])

    return {
      id,
      label,
      searchText,
      raw: project,
    }
  })
}

export function filterPropertyProjectOptions(
  options: PropertyProjectOption[],
  query: string,
  maxResults = 50
): PropertyProjectOption[] {
  const trimmed = query.trim().toLowerCase()
  if (!trimmed) return options.slice(0, maxResults)

  return options
    .filter(
      (option) =>
        option.searchText.includes(trimmed) || option.label.toLowerCase().includes(trimmed)
    )
    .slice(0, maxResults)
}

export function findPropertyProjectOption(
  options: PropertyProjectOption[],
  value: string | null | undefined
): PropertyProjectOption | undefined {
  if (!value) return undefined
  const direct = options.find((option) => option.id === value)
  if (direct) return direct

  if (value.startsWith('project-')) {
    const index = parseInt(value.replace('project-', ''), 10)
    if (!isNaN(index) && options[index]) return options[index]
  }

  return undefined
}

/** Parse leads.projects JSON into ProjectItem array */
export function parseLeadProjects(raw: unknown): ProjectItem[] {
  if (!raw) return []
  if (Array.isArray(raw)) return raw as ProjectItem[]
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw)
      return Array.isArray(parsed) ? parsed : [parsed]
    } catch {
      return []
    }
  }
  if (typeof raw === 'object') return [raw as ProjectItem]
  return []
}

/** Resolve a lead project by stored id or address fallback */
export function resolveLeadProject(
  projects: ProjectItem[],
  leadProjectId: string | null | undefined,
  propertyAddress: string | null | undefined
): ProjectItem | null {
  if (leadProjectId) {
    const byId = projects.find((p) => p.id && String(p.id) === String(leadProjectId))
    if (byId) return byId
    if (String(leadProjectId).startsWith('project-')) {
      const index = parseInt(String(leadProjectId).replace('project-', ''), 10)
      if (!isNaN(index) && projects[index]) return projects[index]
    }
  }
  if (propertyAddress) {
    const byAddress = projects.find((p) => p.address && p.address === propertyAddress)
    if (byAddress) return byAddress
  }
  return null
}

/** Resolve site address for DSNY / consistency checks from customer property or lead project. */
export function resolveProposalSiteAddress(params: {
  entityType: 'customer' | 'lead'
  propertyId: string
  customerProperties?: PropertyItem[]
  leadProjects?: ProjectItem[]
  fallbackAddress?: string | null
}): string {
  const {
    entityType,
    propertyId,
    customerProperties = [],
    leadProjects = [],
    fallbackAddress,
  } = params

  if (entityType === 'customer' && propertyId) {
    const property = customerProperties.find((p) => p.id === propertyId)
    if (property?.address?.trim()) return property.address.trim()
  }

  if (entityType === 'lead' && propertyId && leadProjects.length > 0) {
    const matched = resolveLeadProject(leadProjects, propertyId, fallbackAddress)
    if (matched?.address?.trim()) return matched.address.trim()
  }

  return fallbackAddress?.trim() || ''
}
